import React, { createContext, useContext, useMemo } from 'react';

const AiContext = createContext();

/**
 * AiProvider - Provides AI request handlers to the editor
 *
 * @param {Object} props
 * @param {Function} props.handleAiRequest - Function to handle AI text requests
 * @param {Function} props.handleVoiceRequest - Function to handle voice-to-text requests
 * @param {React.ReactNode} props.children
 *
 * @example
 * // In your app root:
 * const handleAiRequest = async ({ template, selectedText, customPrompt, customFileData }) => {
 *   const { data: response } = await supabase.functions.invoke('ai/text-completion', {
 *     body: {
 *       model: AI_CONFIG.MODEL,
 *       details: { template },
 *       input: { content: selectedText, prompt: customPrompt },
 *       ...(customFileData && { file: customFileData })
 *     }
 *   });
 *   return response;
 * };
 *
 * <AiProvider handleAiRequest={handleAiRequest} handleVoiceRequest={handleVoiceRequest}>
 *   <App />
 * </AiProvider>
 */
export const AiProvider = ({
  children,
  handleAiRequest,
  handleVoiceRequest
}) => {
  const value = useMemo(
    () => ({ handleAiRequest, handleVoiceRequest }),
    [handleAiRequest, handleVoiceRequest]
  );

  return <AiContext.Provider value={value}>{children}</AiContext.Provider>;
};

/**
 * useAi - Hook to access AI request handlers
 * Must be used within an AiProvider
 *
 * @returns {{ handleAiRequest: Function, handleVoiceRequest: Function }}
 */
export const useAi = () => {
  const context = useContext(AiContext);
  if (!context) {
    throw new Error('useAi must be used within an AiProvider');
  }
  return context;
};
